import React, { useCallback, useMemo, useState } from 'react'
import Dropdown from '@groovehq/internal-design-system/lib/components/Dropdown/Dropdown'
import Button from '@groovehq/internal-design-system/lib/components/Button/Button'
import Field from '@groovehq/internal-design-system/lib/components/Field/Field'
import NumberField from '@groovehq/internal-design-system/lib/components/NumberField/NumberField'
import Checkbox from '@groovehq/internal-design-system/lib/components/Checkbox/Checkbox'
import { list } from '@groovehq/internal-design-system/lib/styles/elements'
import { useForm } from 'react-hook-form'
import uuid from 'uuid/v4'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import { styles } from './styles'
import Plus from './Plus'

// keys should preferably match the object being stored in the entity store
// that way for updates, you can just spread the entity in reset() and not have to set each property individually
const FORM_SCHEMA = yup.object().shape({
  displayName: yup.string().required('Item name is required'),
  price: yup.number().required('Price is required'),
  quantity: yup.number().required('Quantity is required'),
})

const defaultValues = {
  displayName: '',
  price: null,
  quantity: 1,
  taxable: false,
  requiresShipping: false,
}

const CustomItem = ({ currency, onAddItem, onVisibleChange }) => {
  const {
    formState: { isValid },
    register,
    reset,
    getValues,
  } = useForm({
    mode: 'all',
    resolver: yupResolver(FORM_SCHEMA),
    defaultValues,
  })

  const handleSave = useCallback(
    () => {
      const data = getValues()
      const payload = { ...data, id: uuid(), custom: true }
      onAddItem(payload)
      onVisibleChange(false)
    },
    [getValues, onAddItem, onVisibleChange]
  )

  const handleCancel = useCallback(
    () => {
      reset(defaultValues)
      onVisibleChange(false)
    },
    [reset, onVisibleChange]
  )

  const quantity = register('quantity')
  const handleQuantityChange = useCallback(
    e => {
      const fake = {
        target: {
          name: e.target.name,
          value: +e.target.value,
        },
      }
      quantity.onChange(fake)
    },
    [quantity]
  )

  const price = register('price')
  const handlePriceChange = useCallback(
    e => {
      const fake = {
        target: {
          name: e.target.name,
          value: +e.target.value,
        },
      }
      price.onChange(fake)
    },
    [price]
  )

  const footer = (
    <>
      <Button type="tertiary" size="small" onClick={handleCancel}>
        Cancel
      </Button>
      <Button size="small" disabled={!isValid} onClick={handleSave}>
        Save line item
      </Button>
    </>
  )

  const overlay = useMemo(
    () => {
      return (
        <div className="grui px-7 py-6" css={styles.overlay}>
          <ul className="grui mb-10" css={list.styles.preflight}>
            <li>
              <Field label="Line item name" {...register('displayName')} />
            </li>
            <li>
              <NumberField
                label="Price per item"
                tagPosition="left"
                tagContent={currency}
                name={price.name}
                value={price.value}
                onChange={handlePriceChange}
              />
            </li>
            <li>
              <NumberField
                label="Quantity"
                name={quantity.name}
                value={quantity.value}
                onChange={handleQuantityChange}
              />
            </li>
          </ul>
          <Checkbox
            id="item-taxable"
            className="grui mb-5"
            {...register('taxable')}
          >
            Item is taxable
          </Checkbox>
          <Checkbox id="item-shipping" {...register('requiresShipping')}>
            Item requires shipping
          </Checkbox>
        </div>
      )
    },
    [
      currency,
      register,
      quantity,
      handleQuantityChange,
      handlePriceChange,
      price,
    ]
  )

  return (
    <Dropdown
      overlay={overlay}
      footer={footer}
      footerWithBtns
      hasMinWidth
      autoHeight
      visible
      onVisibleChange={onVisibleChange}
    >
      <Button
        type="tertiary"
        size="small"
        className="grui ml-5"
        customIcon={<Plus />}
      >
        Add custom item
      </Button>
    </Dropdown>
  )
}

const CustomItemOuter = props => {
  const [isFormVisible, setFormVisible] = useState(false)

  const onVisibleChange = useCallback(
    newVal => {
      setFormVisible(newVal)
    },
    [setFormVisible]
  )

  const setVisible = useCallback(
    () => {
      setFormVisible(true)
    },
    [setFormVisible]
  )

  if (isFormVisible) {
    return <CustomItem {...props} onVisibleChange={onVisibleChange} />
  }

  return (
    <Button
      type="tertiary"
      size="small"
      className="grui ml-5"
      customIcon={<Plus />}
      onClick={setVisible}
    >
      Add custom item
    </Button>
  )
}

// CustomItem.propTypes = {
//   currency: string,
// }

// CustomItem.defaultProps = {
//   currency: '',
//   onAddItem() {},
// }

export default CustomItemOuter
